// ★ 型は SDK から取る。web は core に依存しない（`lint:deps` が落とす）。
import type { EnrollmentStep, EnrollmentSteps } from "@rca/sdk";
import { Code } from "../docs/Code.js";

/**
 * 手順を描く。
 *
 * ★ **文面を持たない。** 出どころは `CloudAccess.enrollment()` だけで、
 * この部品は受け取ったものを並べるだけ。
 *
 * ★ **画面とマニュアルが同じ部品を使う。** 書き分けると必ずずれる——
 * `SupportView` が `location.origin` を出していて、置き場所が分かれた
 * 瞬間に嘘になるのと同じ形。見張りは `deploy-doc.test.tsx`。
 */

export function StepList({
  steps,
  heading,
}: {
  readonly steps: readonly EnrollmentStep[];
  readonly heading: string;
}): React.JSX.Element {
  return (
    <section className="doc-section">
      <h3>{heading}</h3>
      <ol className="deploy-steps">
        {steps.map((step) => (
          <StepBody key={step.title} step={step} />
        ))}
      </ol>
    </section>
  );
}

function StepBody({ step }: { readonly step: EnrollmentStep }): React.JSX.Element {
  return (
    <li className="deploy-step">
      <h4>{step.title}</h4>
      {/* ★ なぜ要るのか。顧客の管理者が承認できるように、必ず出す。 */}
      <p className="deploy-why">{step.why}</p>
      {/* ★ 誰の権限が要るか。承認の重さが読める。 */}
      <p className="deploy-requires">
        <span className="deploy-requires-label">要る権限</span>
        <span>{step.requires}</span>
      </p>
      {step.kind === "link" && step.link !== undefined ? (
        <p>
          <a className="deploy-link" href={step.link} target="_blank" rel="noreferrer noopener">
            開く
          </a>
        </p>
      ) : null}
      {step.kind === "command" && step.command !== undefined ? (
        <Code>{step.command.join("\n")}</Code>
      ) : null}
    </li>
  );
}

/** 実行時に要る役割。★ ここに広いものが出てこないことが、顧客への説明になる。 */
export function RoleList({ roles }: { readonly roles: readonly string[] }): React.JSX.Element {
  return (
    <section className="doc-section">
      <h3>実行時に要る権限</h3>
      <p>
        ★ <strong>これだけです。</strong> 一式を作る権限（<code>Contributor</code> や{" "}
        <code>Owner</code>）は要求しません。作るのはお客様の作業なので、我々には要りません。
        つまり我々の資格が漏れても、お客様のリソースを消すことはできません。
      </p>
      <ul className="deploy-roles">
        {roles.map((role) => (
          <li key={role}>
            <code>{role}</code>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** 手順・役割・切り方をまとめて描く。★ 画面もマニュアルもこれを呼ぶ。 */
export function EnrollmentBody({
  enrollment,
}: {
  readonly enrollment: EnrollmentSteps;
}): React.JSX.Element {
  return (
    <>
      <StepList heading="入れる手順" steps={enrollment.steps} />
      <RoleList roles={enrollment.roles} />
      {/* ★ 切り方。我々に連絡せずに切れることが、この方式の売り。 */}
      <StepList heading="やめるとき（我々への連絡は要りません）" steps={enrollment.revoke} />
    </>
  );
}
